import { is } from '../is/is'
import { Object } from '../Object/Object'

import Logger from '../Logger/Logger'

import type { TCallback, IPostMessage, TPostMessageData, IPostMessageWithCommand } from './definitions'

export interface IPostMessageHandler<TCommand extends string = string> {
  readonly on:    (command: TCommand, callback: TCallback) => void
  readonly once:  (command: TCommand, callback: TCallback) => void
  readonly off:   (command: TCommand, callback: TCallback) => void
  readonly emit:  (command: TCommand, data?: TPostMessageData, w?: Window, targetOrigin?: string) => void
  readonly clear: (command?: TCommand) => void
}

type TCommands<TCommand extends string> = Partial<Record<TCommand, TCallback[]>>

export default class PostMessageHandler<TCommand extends string = string> implements IPostMessageHandler <TCommand> {
  public logger = new Logger('PostMessageHandler')
  protected commands: TCommands<TCommand> = {}

  public constructor () {
    window.addEventListener('message', this.onMessage)
  }

  /**
   * Fel vagyunk iratkozva a Commandra?
   * @param command - Command.
   */
  public isSubscribedOnCommand (command: TCommand): boolean {
    return is.defined(this.commands[command])
  }

  /**
   * Egy command hallgatása.
   * @param command  - Command.
   * @param callback - A callback, ami meg lesz hívva.
   */
  public on (command: TCommand, callback: TCallback): void {
    if (!this.isSubscribedOnCommand(command)) {
      this.commands[command] = []
    }

    this.commands[command]?.push(callback)
  }

  /**
   * Egy command hallgatása, de csak egyszer.
   * Ha elsül, leiratkozik róla automatikusan.
   * @param command  - Command.
   * @param callback - A callback, ami meg lesz hívva.
   */
  public once (command: TCommand, callback: TCallback): void {
    /**
     * Dummy callback, amiről egyből le is iratkozunk.
     * @param postMessage - A postMessage object, amit tovább passzolunk.
     */
    const cb = (postMessage: IPostMessage): void => { // eslint-disable-line no-restricted-syntax -- A this miatt kell.
      callback(postMessage)
      this.off(command, cb)
    }

    this.on(command, cb)
  }

  /**
   * Leiratkozás egy függvényről.
   * @param command           - Command.
   * @param removableCallback - A callback, amit leveszünk.
   */
  public off (command: TCommand, removableCallback: TCallback): void {
    const callbacks = this.commands[command]

    if (is.undefined(callbacks) || is.empty(callbacks)) {
      return
    }

    const remainingCallbacks = callbacks.filter((callback) => {
      return callback !== removableCallback
    })

    if (is.empty(remainingCallbacks)) {
      this.clearCommand(command)

      return
    }

    this.commands[command] = remainingCallbacks
  }

  /**
   * Egy command kilövése.
   * @param command      - Command.
   * @param data         - Data.
   * @param w            - Window.
   * @param targetOrigin - TargetOrigin.
   * @throws Strict módban, ha az adott eseményhez nem tartozik callback, akkor hibát dobunk.
   */
  public emit (
    command: TCommand,
    data?: TPostMessageData,
    w: Window = window,
    targetOrigin = '*'
  ): void {
    const message = { command, data }

    // this.logger.dev(command, message) // HACK
    this.logger.log('<emit>', command, message)

    w.postMessage(message, targetOrigin)
  }

  /**
   * Egy vagy az összes commandhoz tartozó callback-ek törlése.
   * @param command - Command.
   */
  public clear (command?: TCommand): void {
    if (command) {
      this.clearCommand(command)

      return
    }

    Object.getKeys(this.commands).forEach((cmd) => {
      this.clearCommand(cmd)
    })
  }

  /**
   * Egy command törlése.
   * @param command - Command.
   */
  private clearCommand (command: TCommand): void {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- Itt szükséges törölni.
    delete this.commands[command]
  }

  private readonly onMessage = async (
    event: MessageEvent<IPostMessageWithCommand<TPostMessageData, TCommand>>
  ): Promise<void> => {
    const message = event.data

    const { command, data } = message

    if (is.undefined(command)) {
      return
    }

    this.logger.log('<onMessage>', command, message)

    const callbacks = this.commands[command] ?? []

    if (is.empty(callbacks)) {
      return
    }

    await Promise.all(
      callbacks.map((cb) => {
        const res = cb({ data })

        if (is.instanceOf(res, Promise)) {
          return res
        }

        return Promise.resolve()
      })
    )
  }
}
